/**
 * ModelSelectorField Component
 *
 * Rich model selector field with pricing, provider info, and search/filter.
 * Supports integrated linked parameters (temperature, top_p, mode, etc.) for compact layout.
 * Supports feature toggle for optional features (vision, stt, reranking) with hooks sync.
 */

import { useMemo, useCallback } from 'react';
import { FieldSchema, ValidationResult, FIELD_CONFIG, getConfigValue } from '@/config/fieldConfig';
import { EnhancedModelSelector } from '@/components/ModelSelector';
import type { ModelInfo as ApiModelInfo } from '@/types/models';
import type { ModelInfo as SelectorModelInfo, LinkedParameter, FeatureToggleProps } from '@/components/ModelSelector/types';
import { useI18n } from '@/i18n';
import { isHookEnabled, getHook, type HookId } from '@/config/hooks-registry';

export interface ModelSelectorFieldProps {
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: Record<string, unknown>;
  /** Full form data for reading/writing linked parameters */
  formData?: Record<string, unknown>;
  /** Callback to update form data (for linked parameters) */
  onFormDataChange?: (path: string, value: unknown) => void;
  /** Use compact inline display (minimal row instead of card) */
  compact?: boolean;
}

/**
 * Convert API ModelInfo to ModelSelector's ModelInfo format
 */
function toSelectorModelInfo(model: ApiModelInfo): SelectorModelInfo {
  return {
    id: model.id,
    display_name: model.display_name || model.id,
    provider: model.provider || null,
    supports_vision: model.supports_vision || false,
    supports_reasoning: model.supports_reasoning || false,
    max_input_tokens: model.max_input_tokens || null,
    pricing: model.pricing || null,
    estimated_cost: model.estimated_cost || null,
    categories: model.categories || [],
  };
}

export const ModelSelectorField: React.FC<ModelSelectorFieldProps> = ({
  value,
  onChange,
  schema,
  error,
  disabled = false,
  appContext,
  formData,
  onFormDataChange,
  compact = false,
}) => {
  const { getFieldTranslation, language } = useI18n();

  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const help = schema.help && typeof schema.help !== 'object'
    ? (typeof schema.help === 'function' ? schema.help() : schema.help)
    : undefined;

  // Get models from app context based on optionsSource
  const models = useMemo<SelectorModelInfo[]>(() => {
    const dynamicOptionsSource = schema.getOptionsSource
      ? schema.getOptionsSource(appContext)
      : schema.optionsSource;

    if (dynamicOptionsSource && appContext) {
      const modelsArray = appContext[dynamicOptionsSource] as ApiModelInfo[] | undefined;
      if (Array.isArray(modelsArray)) {
        return modelsArray.map(toSelectorModelInfo);
      }
    }

    return [];
  }, [schema, appContext]);

  // Build linked parameters from schema.linkedParameters
  const linkedParameters = useMemo<LinkedParameter[]>(() => {
    if (!schema.linkedParameters || !formData) return [];

    return schema.linkedParameters
      .map((paramName) => {
        const paramSchema = FIELD_CONFIG[paramName];
        if (!paramSchema) return null;

        // NOTE: We intentionally do NOT check showIf here.
        // Fields explicitly listed in linkedParameters should always be shown
        // in the model selector's parameters section, even if they have
        // showIf: () => false (which hides them from standalone display).

        // Get translated label
        const translation = getFieldTranslation(paramName);
        const paramLabel = translation?.label ||
          (typeof paramSchema.label === 'function' ? paramSchema.label() : paramSchema.label);

        // Get help text
        const currentValue = getConfigValue(formData, paramName);
        let paramHelp: string | undefined;
        if (paramSchema.help) {
          if (typeof paramSchema.help === 'object' && typeof currentValue === 'string') {
            paramHelp = paramSchema.help[currentValue];
          } else if (typeof paramSchema.help === 'string') {
            paramHelp = paramSchema.help;
          } else if (typeof paramSchema.help === 'function') {
            paramHelp = paramSchema.help();
          }
        }

        // Determine parameter type
        let paramType: 'number' | 'select' | 'button_group' = 'number';
        if (paramSchema.type === 'select') {
          paramType = paramSchema.buttonGroup ? 'button_group' : 'select';
        }

        // Get options with translations
        let options: Array<{ value: string; label: string }> | undefined;
        if (paramSchema.options) {
          options = paramSchema.options.map((opt) => ({
            value: opt.value,
            label: translation?.options?.[opt.value] || opt.label,
          }));
        }

        return {
          name: paramName,
          label: paramLabel,
          type: paramType,
          value: currentValue,
          options,
          min: paramSchema.min,
          max: paramSchema.max,
          step: paramSchema.step,
          help: paramHelp,
        } as LinkedParameter;
      })
      .filter((p): p is LinkedParameter => p !== null);
  }, [schema, formData, getFieldTranslation]);

  // Handle model value change
  const handleChange = (newValue: string | null) => {
    onChange(newValue === '' ? null : newValue);
  };

  // Handle linked parameter changes
  const handleParameterChange = useCallback((paramName: string, paramValue: unknown) => {
    onFormDataChange?.(paramName, paramValue);
  }, [onFormDataChange]);

  // Feature toggle support for optional features (vision, stt, reasoning, reranking)
  const featureToggle = useMemo<FeatureToggleProps | undefined>(() => {
    if (!schema.hasFeatureToggle || !schema.featureHookId) return undefined;

    const hookId = schema.featureHookId as HookId;
    const hook = getHook(hookId);
    if (!hook) return undefined;

    // For hooks with allowEmpty (like reasoning), read enabled state from hooks.*.enabled
    // For hooks without allowEmpty (like vision, stt), enabled state = model_id is set
    const useSeparateEnabledFlag = schema.allowEmpty && hook.configPath.includes('.enabled');

    let featureEnabled: boolean;
    if (useSeparateEnabledFlag && formData) {
      // Read from hooks.*.enabled (e.g., hooks.reasoning.enabled)
      featureEnabled = Boolean(getConfigValue(formData, hook.configPath));
    } else {
      // Determine if feature is enabled based on the model value
      featureEnabled = isHookEnabled(hookId, value);
    }

    return {
      featureEnabled,
      onFeatureToggle: (enabled: boolean) => {
        if (useSeparateEnabledFlag) {
          // Update hooks.*.enabled flag (e.g., hooks.reasoning.enabled)
          onFormDataChange?.(hook.configPath, enabled);
          // Don't change model_id - user can select empty (native) or specific model (combined)
        } else {
          // Legacy behavior: toggle by setting/clearing model_id
          if (enabled) {
            const defaultModel = schema.defaultEnabledModel || (models.length > 0 ? models[0].id : null);
            onChange(defaultModel);
          } else {
            onChange(null);
          }
        }
      },
      featureLabel: hook.displayName,
    };
  }, [schema.hasFeatureToggle, schema.featureHookId, schema.defaultEnabledModel, schema.allowEmpty, value, models, formData, onChange, onFormDataChange]);

  // Determine if empty selection is allowed
  // For feature toggle fields with allowEmpty (like reasoning): allow empty when enabled
  // For feature toggle fields without allowEmpty (like vision, stt): don't allow empty
  // For non-toggle fields: use schema setting
  const allowEmpty = schema.hasFeatureToggle
    ? (schema.allowEmpty && featureToggle?.featureEnabled)
    : (schema.allowEmpty || !schema.required);
  const emptyLabel = schema.emptyLabel || (language === 'zh' ? '使用系统默认' : 'Use system default');
  const isPrimary = schema.isPrimary || false;

  return (
    <div className="field-wrapper">
      <EnhancedModelSelector
        value={value || null}
        onChange={handleChange}
        models={models}
        label={label}
        sublabel={compact ? undefined : help}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
        disabled={disabled}
        isPrimary={isPrimary}
        linkedParameters={compact ? undefined : linkedParameters}
        onParameterChange={onFormDataChange ? handleParameterChange : undefined}
        featureToggle={compact ? undefined : featureToggle}
        inline={compact}
      />

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}
    </div>
  );
};

export default ModelSelectorField;
